/**
 * Return-path (post-login deep-link) utilities.
 *
 * When an unauthenticated user hits a protected page, the layout guard redirects
 * them to login. These helpers capture the page they were trying to reach so the
 * login flow can send them back there after they authenticate, instead of always
 * dumping them on the dashboard overview.
 *
 * sessionStorage (not router state) is the source of truth: it survives a full
 * page reload and the external SSO round-trip, and clears automatically when the
 * tab closes.
 */

const STORAGE_KEY = 'pa_return_path';

// Routes that must never be used as a post-login destination, to avoid redirect
// loops (bouncing back to login/callbacks after authenticating).
const DISALLOWED_PREFIXES = [
  '/login',
  '/callback',
  '/okta/callback',
  '/register',
  '/reset_password',
];

/**
 * Validate that a path is a safe same-origin, relative destination.
 *
 * Only accepts app-relative paths beginning with a single "/". Rejects absolute
 * URLs, protocol-relative "//host" paths, and the auth/callback routes above.
 * This is the open-redirect guard: anything replayed after login must originate
 * from our own routing, never from an attacker-controlled value.
 *
 * @param {string} path - Candidate path (may include query/hash).
 * @returns {boolean} True if the path is safe to navigate to after login.
 */
export const isSafeReturnPath = path => {
  if (!path || typeof path !== 'string') return false;

  // Must be app-relative: single leading slash, not "//" (protocol-relative)
  // and not an absolute URL like "http://" or "https:".
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  if (path.includes('://')) return false;

  // Reject backslashes: browsers normalize "\" to "/", so "/\evil.com" or
  // "/\/evil.com" would resolve to a protocol-relative authority and defeat the
  // "//" guard above.
  if (path.includes('\\')) return false;

  // "/" is not a real destination: the non-reskinned route table redirects it
  // straight back to /login, so treat a bare root as absent.
  if (path === '/') return false;

  // Compare the auth/callback routes against the normalized pathname only
  // (case-insensitive, query/hash stripped) so variants like "/login?x=1",
  // "/login#x" or "/LOGIN" can't slip past and cause a redirect loop.
  const pathname = path.split(/[?#]/)[0].toLowerCase();
  return !DISALLOWED_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
};

/**
 * Persist the intended destination so it can be replayed after login.
 *
 * No-ops for unsafe paths so a bad value can never be stored (and therefore never
 * replayed). Overwrites any previously stored path with the most recent attempt.
 *
 * @param {string} path - The app-relative path the user was trying to reach.
 */
export const saveReturnPath = path => {
  try {
    if (!isSafeReturnPath(path)) return;
    window.sessionStorage.setItem(STORAGE_KEY, path);
  } catch (error) {
    // sessionStorage can throw in private mode / when disabled; deep-link return
    // is a best-effort enhancement, so fail silently.
    console.warn('Unable to save return path:', error);
  }
};

/**
 * Read and clear the stored return path in a single call.
 *
 * Returns the stored path only if it is still safe, otherwise null. Always clears
 * the stored value so a stale destination can't leak into a later login.
 *
 * @returns {string|null} The safe return path, or null if none/invalid.
 */
export const consumeReturnPath = () => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return isSafeReturnPath(stored) ? stored : null;
  } catch (error) {
    console.warn('Unable to read return path:', error);
    return null;
  }
};

/**
 * Build the current app-relative location (path + query + hash).
 *
 * Convenience for callers (e.g. the layout guard) that want to capture "where the
 * user is right now" without assembling it by hand.
 *
 * @returns {string} The current pathname, search, and hash concatenated.
 */
export const getCurrentPath = () => {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
};

/**
 * The default post-login destination when no deep link is in play.
 *
 * @param {boolean} showReskin - The showReskin feature flag.
 * @returns {string} '/overview' for the reskinned experience, otherwise '/Dash'.
 */
export const getDefaultDestination = showReskin =>
  showReskin ? '/overview' : '/Dash';

/**
 * Resolve the post-login destination in a single place, in priority order:
 *   1. A redirect the BFF passed back (only used when safe) — the callback flow
 *      supplies this; the plain login form does not.
 *   2. A deep-link path saved before we bounced the user to login.
 *   3. The flag-based dashboard fallback.
 *
 * Consumes (reads and clears) the saved return path, so call this exactly once
 * per login. Every candidate is validated by isSafeReturnPath, so an
 * attacker-controlled redirect value can never trigger an open redirect.
 *
 * @param {Object} [options]
 * @param {string} [options.redirectParam] - Already-decoded redirect value.
 * @param {boolean} [options.showReskin] - The showReskin feature flag.
 * @returns {string} A safe destination path to navigate to.
 */
export const resolvePostLoginDestination = ({
  redirectParam,
  showReskin,
} = {}) => {
  const savedReturnPath = consumeReturnPath();
  if (isSafeReturnPath(redirectParam)) return redirectParam;
  return savedReturnPath || getDefaultDestination(showReskin);
};

/**
 * Capture where the user is right now so the login flow can return them here,
 * for the asynchronous session-expiry paths (a 401 on a background request, or a
 * token that expires while the tab is open). Those paths hard-redirect via
 * window.location and never pass through the layout guard, so without this the
 * user loses the page they were on — exactly the case this feature addresses.
 *
 * No-ops for unsafe paths (see saveReturnPath).
 */
export const saveCurrentReturnPath = () => {
  saveReturnPath(getCurrentPath());
};
